import { useEffect, useState } from "react";
import { Check, CheckCheck, Pencil, Search } from "lucide-react";
import { FormWrapper } from "@/components/molecules/FormWrapper/FormWrapper";
import { FormSelect } from "@/components/molecules/FormSelect/FormSelect";
import { ClientOrderFormProps } from "./ClientOrdersForm.types";
import { ClientOrderFormData, clientOrderSchema, clientVariantMap, identificationTypeOptions, rentTypeLabels } from "../infrastructure/ClientOrdersConstants";
import { mapClientOrderToFormValues } from "../infrastructure/ClientOrderMapper";
import { InstrumentDetail } from "@/modules/orders/presentation/InstrumentDetail";
import { FormInput } from "@/components/molecules/FormInput/FormInput";
import { FormAutocomplete } from "@/components/molecules/FormAutocomplete/FormAutocomplete";
import { Badge } from "@/components/atoms/Badge";
import { ClientOrderStatusEnum, ClientInstrumentValidationEnum, ClientRate, ClientOrderTypeEnum, Client, RentTypeEnum } from "../domain/entities/ClientOrdersEntity";
import { Button } from "@/components/atoms/Button";
import { useConfirmDialogs } from "@/core/context/DialogContext";
import { FormSwitch } from "@/components/molecules/FormSwitch";
import { Switch } from "@/components/atoms/Switch";
import { externalApiService } from "@/core/infrastructure/ExternalApiService";
import { Loading } from "@/components/atoms/Loading/Loading";
import { LimitTypeEnum } from "@/modules/orders/domain/entities/OrdersEntity";

export const ClientOrdersForm = ({
    onSubmit,
    onDelete,
    onContinue,
    clientOrderSelected,
    clientOptions: _clientOptions,
    clientInstrumentOptions,
    clientInstrumentSelected,
    clientRates,
    clients: _clients,
    setClientInstrumentSelected,
    setClientShareInstrumentSelected,
    clientShareInstrumentSelected,
    clientShareInstrumentOptions,
    portfolios,
    formStatus
}: ClientOrderFormProps) => {
    const [isOn, setIsOn] = useState(clientOrderSelected ? clientOrderSelected.orderType === ClientOrderTypeEnum.COMPRA : true);
    const [isRentFixed, setIsRentFixed] = useState(clientOrderSelected ? clientOrderSelected.rentType === RentTypeEnum.RF : true);
    const [ratesByInstrument, setRatesByInstrument] = useState<ClientRate[] | undefined>([]);
    const [clientSelected, setClientSelected] = useState<Client | undefined>(undefined);
    const [limitType, setLimitType] = useState(clientOrderSelected ? clientOrderSelected.limitType === LimitTypeEnum.MERCADO : true);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const variant = clientVariantMap[clientOrderSelected?.status as unknown as ClientOrderStatusEnum] ?? "info";
    const showDialog = useConfirmDialogs();

    useEffect(() => {
        setRatesByInstrument(clientRates.filter(r => r.instrumentId === clientInstrumentSelected?.id));
    }, [clientInstrumentSelected, clientRates]);

    // Function to search for client by identification
    const searchClient = async (identification: string, identificationType: string, setValue?: (name: 'client', value: string) => void) => {
        if (!identification || !identificationType) {
            setSearchError("Por favor ingrese el número y tipo de identificación");
            console.warn("⚠️ Por favor ingrese el número y tipo de identificación");
            return;
        }

        setIsSearching(true);
        setSearchError(null);

        try {
            // console.log("🔍 Buscando cliente:", { identification, identificationType });

            const response = await externalApiService.getCustomerByIdentification<Client[]>(
                identification,
                identificationType
            );

            // console.log("📦 Respuesta de búsqueda:", response);

            // API returns an array, get the first customer
            if (Array.isArray(response) && response.length > 0) {
                const customer = response[0];
                // console.log("✅ Cliente encontrado:", customer);
                setClientSelected(customer);
                setSearchError(null);
                // Set the client field value for form validation
                if (setValue) {
                    setValue('client', customer.identificacion);
                }
            } else if (response && !Array.isArray(response)) {
                // Handle case where API returns a single object instead of array
                // console.log("✅ Cliente encontrado (objeto único):", response);
                const customer = response as unknown as Client;
                setClientSelected(customer);
                setSearchError(null);
                if (setValue) {
                    setValue('client', customer.identificacion);
                }
            } else {
                // console.warn("⚠️ No se encontró cliente con esa identificación");
                setClientSelected(undefined);
                setSearchError("No se encontró cliente con esa identificación");
                if (setValue) {
                    setValue('client', '');
                }
            }

        } catch (error) {
            console.error("❌ Error al buscar cliente:", error);
            setClientSelected(undefined);
            const errorMessage = error instanceof Error ? error.message : "Ocurrió un error en la consulta del cliente";
            setSearchError(errorMessage);
        } finally {
            setIsSearching(false);
        }
    };

    // Auto-search client when editing an existing order
    useEffect(() => {
        // Reset client selected when order changes
        setClientSelected(undefined);
        setSearchError(null);

        if (clientOrderSelected?.client?.identificacion && clientOrderSelected?.client?.tipoIdentificacion) {
            // Fetch fresh client data from API
            searchClient(clientOrderSelected.client.identificacion, clientOrderSelected.client.tipoIdentificacion);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientOrderSelected]);

    const confirmSubmit = async (formData: ClientOrderFormData) => {
        if (!formData.orderType) {
            onSubmit(formData)
        } else {
            let confirm = false;

            // Only fixed-income instruments have validation codes today.
            // Variable-income (shares) will submit directly unless we later add validation to that model.
            const instrumentValidation = isRentFixed
                ? (clientInstrumentSelected?.validation as ClientInstrumentValidationEnum | undefined)
                : undefined;

            switch (instrumentValidation) {
                case ClientInstrumentValidationEnum.V1:
                    confirm = await showDialog({
                        title: "Validación Exitosa de Límites",
                        description: "La transacción cumple con la validación de límites y políticas.",
                        variant: "success",
                    });
                    break;
                case ClientInstrumentValidationEnum.V2:
                    confirm = await showDialog({
                        title: "Validación de Límites No Exitosa",
                        description: (
                            <div className="flex-col space-y-2">
                                <p>
                                    La transacción no cumple con el límite de concentración por Calificación, el instrumento es inferior a AA+ y excede el % permitido.
                                </p>
                                <p>
                                    ¿Desea gestionar una excepción con el departamento de riesgos?
                                </p>
                            </div>
                        ),
                        variant: "warning",
                    });
                    break;
                case ClientInstrumentValidationEnum.V3:
                    confirm = await showDialog({
                        title: "Validación de Límites No Exitosa",
                        description: (
                            <div className="flex-col space-y-2">
                                <p>
                                    La transacción no cumple con el límite de concentración por plazo, el instrumento es superior a 1,800 días y excede el % permitido.
                                </p>
                                <p>
                                    ¿Desea gestionar una excepción con el departamento de riesgos?
                                </p>
                            </div>
                        ),
                        variant: "warning",
                    });
                    break;
                case ClientInstrumentValidationEnum.V4:
                    confirm = await showDialog({
                        title: "Validación de Límites No Exitosa",
                        description: (
                            <div className="flex-col space-y-2">
                                <p>
                                    La transacción no cumple con el límite de concentración por emisor, la posición en ese emisor excedería el % permitido.
                                </p>
                                <p>
                                    ¿Desea gestionar una excepción con el departamento de riesgos?
                                </p>
                            </div>
                        ),
                        variant: "warning",
                    });
                    break;

                default:
                    break;
            }

            if (confirm || !instrumentValidation) {
                onSubmit(formData)
            }

        }
    }

    const confirmDelete = async (id: string) => {
        const confirm = await showDialog({
            title: "¿Estás seguro?",
            description: "Esta acción eliminará el registro. ¿Deseas continuar?",
            variant: "delete",
        });

        if (confirm) {
            onDelete(id);
        }
    }

    return (
        <FormWrapper<ClientOrderFormData>
            defaultValues={mapClientOrderToFormValues(clientOrderSelected)}
            onSubmit={confirmSubmit}
            schema={clientOrderSchema}
            formStatus={formStatus}
            onDelete={confirmDelete}
            canSave={clientOrderSelected?.status !== ClientOrderStatusEnum.FIN}
        >
            {({ setValue, getValues }) => {
                const handleRateBlur = () => {
                    const inputRate = Number.parseFloat(getValues("rate")?.toString() ?? "0");
                    const matched = ratesByInstrument?.find(r => r.rate === inputRate);

                    if (matched) {
                        setValue("value", getValues("quantity") * (matched.price / 100));
                    } else {
                        setValue("value", 0);
                    }
                };

                const handleOnLimitTypeChange = (limitType: boolean) => {
                    setLimitType(limitType);
                    if (limitType) {
                        setValue("limitPrice", 0);
                        setValue("value", 0);
                    }
                }

                const clientPortfolio = clientSelected
                    ? portfolios.find(p =>
                        p.name.toLowerCase() === clientSelected.nombre.toLowerCase()
                    )
                    : undefined;

                return (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <FormSwitch
                                disabled
                                label={isOn ? ClientOrderTypeEnum.COMPRA : ClientOrderTypeEnum.VENTA}
                                variant="darkInfo"
                                inactiveVariant="darkError"
                                onCheckedChange={setIsOn}
                                name="orderType" />

                            {clientOrderSelected && (
                                <div className="flex flex-col-reverse md:flex-row md:flex-wrap justify-end gap-2 items-center">
                                    {clientOrderSelected?.confirmNumber && (
                                        <Badge className="w-full md:w-auto gap-2 px-4 justify-between" variant="secondary">
                                            {`N° Operación: ${clientOrderSelected?.confirmNumber}`}
                                            {clientOrderSelected?.status === ClientOrderStatusEnum.COMPLEMENTACION && (
                                                <Button
                                                    size="xs"
                                                    variant="secondary"
                                                    className="p-1 min-w-0 "
                                                    onClick={() =>
                                                        onContinue(
                                                            mapClientOrderToFormValues(clientOrderSelected) as ClientOrderFormData,
                                                            clientOrderSelected?.status
                                                        )}>
                                                    <Pencil className="h-4 w-4" aria-hidden="true" />
                                                </Button>
                                            )}
                                        </Badge>
                                    )}

                                    <div className="flex gap-2 w-full md:w-auto">
                                        <Badge className="w-full" variant={variant}>
                                            {clientOrderSelected?.status}
                                        </Badge>

                                        {clientOrderSelected?.status !== ClientOrderStatusEnum.FIN && (
                                            <Button
                                                size="xs"
                                                onClick={() =>
                                                    onContinue(
                                                        mapClientOrderToFormValues(clientOrderSelected) as ClientOrderFormData,
                                                        clientOrderSelected?.status === ClientOrderStatusEnum.REGISTRO
                                                            ? ClientOrderStatusEnum.COMPLEMENTACION
                                                            : ClientOrderStatusEnum.FIN
                                                    )
                                                }
                                            >
                                                {clientOrderSelected?.status === ClientOrderStatusEnum.REGISTRO ? (
                                                    <Check className="h-6 w-6" aria-hidden="true" />
                                                ) : (
                                                    <CheckCheck className="h-6 w-6" aria-hidden="true" />
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div >

                        {/* Client Identification Section */}
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center">
                            <FormSelect
                                name="clientIdentificationType"
                                label="Tipo de Identificación"
                                options={identificationTypeOptions}
                            />
                            <FormInput
                                name="clientIdentification"
                                label="Número de Identificación"
                                type="text"
                                placeholder="Ej: 3563958"
                            />
                            <div className="flex items-center md:mt-6">
                                <Button
                                    size="xs"
                                    type="button"
                                    disabled={isSearching}
                                    className={isSearching ? "cursor-wait" : ""}
                                    onClick={async () => {
                                        // Get form values using react-hook-form's getValues
                                        const identification = getValues('clientIdentification');
                                        const identificationType = getValues('clientIdentificationType');
                                        await searchClient(identification, identificationType, setValue);
                                    }}
                                >
                                    <Search className="h-6 w-6" aria-hidden="true" />
                                </Button>
                            </div>
                        </div>

                        {/* Display loading indicator while searching */}
                        {isSearching && (
                            <div className="p-8 bg-gray-50 dark:bg-gray-900/20 rounded-lg border border-gray-200 dark:border-gray-800">
                                <Loading />
                                <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-2">Buscando cliente...</p>
                            </div>
                        )}

                        {/* Display error message if search failed */}
                        {!isSearching && searchError && (
                            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                <p className="text-red-900 dark:text-red-100 text-sm font-medium">{searchError}</p>
                            </div>
                        )}

                        {/* Display client info if found */}
                        {!isSearching && !searchError && clientSelected && (
                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Cliente</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div><span className="font-medium">Nombre:</span> {clientSelected.nombre}</div>
                                    <div><span className="font-medium">Identificación:</span> {clientSelected.identificacion}</div>
                                    <div><span className="font-medium">Email:</span> {clientSelected.email}</div>
                                    <div><span className="font-medium">Celular:</span> {clientSelected.celular}</div>
                                    {clientSelected.address && clientSelected.address.length > 0 && (
                                        <>
                                            <div><span className="font-medium">Dirección:</span> {clientSelected.address[0].direccion}</div>
                                            <div><span className="font-medium">Teléfono:</span> {clientSelected.address[0].telefono}</div>
                                        </>
                                    )}
                                    {clientPortfolio && (
                                        <>
                                            <div><span className="font-medium">Cuenta Deceval:</span> {clientPortfolio.decevalAccount}</div>
                                            <div><span className="font-medium">Cuenta DCV:</span> {clientPortfolio.DCVAccount}</div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Hidden field for client - required by schema */}
                        <FormInput name="client" type="hidden" />

                        {/* Hidden field to store the actual string value */}
                        <FormInput name="rentType" type="hidden" />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <Switch
                                label={isRentFixed ? rentTypeLabels[RentTypeEnum.RF] : rentTypeLabels[RentTypeEnum.RV]}
                                variant="success"
                                inactiveVariant="warning"
                                checked={isRentFixed}
                                onCheckedChange={(checked: boolean) => {
                                    setIsRentFixed(checked);
                                    setValue('rentType', checked ? RentTypeEnum.RF : RentTypeEnum.RV);
                                }}
                            />
                        </div>
                        {isRentFixed ? <>
                            <div className="grid grid-cols-1 md:grid-cols-1 gap-2">
                                <FormAutocomplete
                                    name="instrument"
                                    label="Instrumento"
                                    options={clientInstrumentOptions}
                                    onChange={setClientInstrumentSelected}
                                    renderOption={(option) => (
                                        <span className="flex flex-col text-left h-full">
                                            <small className="font-bold">{option.label}</small>
                                            <small>{option.detail}</small>
                                        </span>
                                    )}
                                />
                                {clientInstrumentSelected && (
                                    <InstrumentDetail instrument={clientInstrumentSelected} />
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <FormInput name="quantity" label="Cantidad" type="number" />
                                <FormInput
                                    name="rate"
                                    label="Tasa de negociación"
                                    type="percent"
                                    onBlur={handleRateBlur}
                                />
                                <FormInput name="value" label="Valor giro" type="number" prefix="$" />
                            </div>
                        </>
                            : <>
                                <div className="grid grid-cols-1 md:grid-cols-1 gap-2">
                                    <FormAutocomplete
                                        name="instrument"
                                        label="Instrumento"
                                        options={clientShareInstrumentOptions}
                                        onChange={setClientShareInstrumentSelected}
                                        renderOption={(option) => (
                                            <span className="flex flex-col text-left h-full">
                                                <small className="font-bold">{option.label}</small>
                                                <small>{option.detail}</small>
                                            </span>
                                        )}
                                    />
                                    {clientShareInstrumentSelected && (
                                        <InstrumentDetail instrument={clientShareInstrumentSelected} />
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    <FormSwitch
                                        label={limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE}
                                        inactiveVariant="secondary"
                                        onCheckedChange={handleOnLimitTypeChange}
                                        name="limitType" />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    <FormInput
                                        name="quantity"
                                        label="Cantidad"
                                        type="number"
                                        onChange={({ target }) => {
                                            const newQuantity = Number.parseFloat(target.value || "0");

                                            // When limitType is LIMITE (boolean false), value = quantity * limitPrice
                                            if (!limitType) {
                                                const currentLimitPrice = Number.parseFloat((getValues("limitPrice")?.toString() ?? "0"));
                                                setValue("value", newQuantity * currentLimitPrice);
                                            }
                                        }}
                                    />
                                    {!limitType && (
                                        <>
                                            <FormInput
                                                name="limitPrice"
                                                label="Precio límite"
                                                type="number"
                                                prefix="$"
                                                onChange={({ target }) => {
                                                    const newLimitPrice = Number.parseFloat(target.value || "0");

                                                    // When limitType is LIMITE (boolean false), value = quantity * limitPrice
                                                    if (!limitType) {
                                                        const currentQuantity = Number.parseFloat((getValues("quantity")?.toString() ?? "0"));
                                                        setValue("value", currentQuantity * newLimitPrice);
                                                    }
                                                }}
                                            />
                                            <FormInput name="value" label="Valor giro" type="number" prefix="$" disabled />
                                        </>
                                    )}
                                </div>
                            </>}
                    </>
                );
            }}
        </FormWrapper >
    );
};
